// The 3D view's move gizmo: an arrow along X, Y and Z on the selection, each
// dragged to move it along its axis (docs/claude/handles.md, *The move gizmo*).
// DOM-free: where its arrows are drawn, the one under the pointer, its paint and
// what it reads while it moves. ui/moveGizmo.ts shows it; handleWork.ts works it.
import {
  type DimStyle,
  type Vec2,
} from '../shared/types';
import { formatDimension } from './dimFormat';
import { distToSegment } from './geometry';
import { type Pt3 } from './handles';

export type GizmoAxis = 'x' | 'y' | 'z';

/** Each axis: its direction (drawing coordinates: plan x, y and the height z),
 *  what its readout calls a move along it, the letter its prompt names, and its
 *  colour (X red, Y green, Z blue, as every 3D package draws them). */
export const GIZMO_AXES: Record<GizmoAxis, { dir: Pt3; label: string; name: string; color: string }> = {
  x: { dir: { x: 1, y: 0, z: 0 }, label: 'ΔX', name: 'X', color: '#e5484d' },
  y: { dir: { x: 0, y: 1, z: 0 }, label: 'ΔY', name: 'Y', color: '#30a46c' },
  z: { dir: { x: 0, y: 0, z: 1 }, label: 'ΔZ', name: 'Z', color: '#3e8ef7' },
};

/** How long the longest arrow is drawn (css px); the shortest still drawn — an
 *  arrow pointing (all but) at the eye is left out, as no drag could read it;
 *  and how near its shaft a press takes it. */
export const GIZMO_PX = 90;
export const GIZMO_MIN_PX = 18;
export const GIZMO_HIT_PX = 7;
/** The share of each arrow, from the hub, that takes no press: where the three
 *  meet, none could be told from the others. */
const HUB = 0.22;

/** One arrow as drawn: its axis, the hub and its tip on the canvas (css px). */
export interface GizmoArrow { axis: GizmoAxis; from: Vec2; to: Vec2 }

/** The arrows of a gizmo standing at `origin`, through the view's `project`
 *  (null behind the eye): one per axis — along `dirs`' direction for it where
 *  given (a cage point's: along the bench, out, up) —, all as long in the drawing — the longest
 *  `GIZMO_PX` on screen, so it keeps its size whatever the zoom —, leaving out
 *  one seen end-on. None when the hub is behind the eye. */
export function gizmoLayout(origin: Pt3, project: (p: Pt3) => Vec2 | null, axes: readonly GizmoAxis[], dirs?: Partial<Record<GizmoAxis, Pt3>>): GizmoArrow[] {
  const o = project(origin);
  if (!o) return [];
  const at = (a: GizmoAxis, mm: number): Vec2 | null => {
    const d = dirs?.[a] ?? GIZMO_AXES[a].dir;
    return project({ x: origin.x + d.x * mm, y: origin.y + d.y * mm, z: origin.z + d.z * mm });
  };
  const PROBE = 100;
  const pxPerMm = Math.max(0, ...axes.map((a) => { const t = at(a, PROBE); return t ? Math.hypot(t.x - o.x, t.y - o.y) / PROBE : 0; }));
  if (!(pxPerMm > 0)) return [];
  const L = GIZMO_PX / pxPerMm;
  const out: GizmoArrow[] = [];
  for (const a of axes) {
    const to = at(a, L);
    if (to && Math.hypot(to.x - o.x, to.y - o.y) >= GIZMO_MIN_PX) out.push({ axis: a, from: o, to });
  }
  return out;
}

/** The arrow whose shaft (past the hub) passes nearest `p` within reach, or -1.
 *  The hub is judged by how far along the arrow `p` falls, not by its distance
 *  to the shaft's start — that reach once took a click beside the hub, meant
 *  for the object behind it, as a press on an arrow. */
export function gizmoArrowAt(arrows: readonly GizmoArrow[], p: Vec2): number {
  let best = -1, bd = GIZMO_HIT_PX;
  arrows.forEach((g, i) => {
    const dx = g.to.x - g.from.x, dy = g.to.y - g.from.y, l2 = dx * dx + dy * dy;
    if (!(l2 > 0) || ((p.x - g.from.x) * dx + (p.y - g.from.y) * dy) / l2 < HUB) return;
    const d = distToSegment(p, { x: g.from.x + dx * HUB, y: g.from.y + dy * HUB }, g.to);
    if (d <= bd) { bd = d; best = i; }
  });
  return best;
}

/** What a move along an axis reads: its delta from the press, whole mm signed,
 *  written in the drawing's dimension style as the handles' figures are
 *  (`formatDimension`: `ΔX +250 mm`, `ΔX +25.0 cm`), and what it caught
 *  (`— Endpoint`). */
export function gizmoReadout(axis: GizmoAxis, mm: number, style: DimStyle, caught?: string): string {
  const r = Math.round(mm);
  return `${GIZMO_AXES[axis].label} ${r > 0 ? '+' : r < 0 ? '-' : ''}${formatDimension(Math.abs(r), style)}${caught ? ` — ${caught}` : ''}`;
}

// ---- The rotate ring --------------------------------------------------------------

/** The ring's size on screen (css px) beside the arrows', and its smoothness. */
const RING_PX = GIZMO_PX * 0.7;
const RING_SEGMENTS = 48;
/** A turn snaps in these steps (degrees) unless Alt frees it. */
export const TURN_STEP = 15;

/** The rotate ring of a gizmo at `origin`: a level circle round it, drawn as a
 *  closed polyline on the canvas (css px), as big on screen as the arrows say —
 *  none when part of it is behind the eye. */
export function ringLayout(origin: Pt3, project: (p: Pt3) => Vec2 | null): Vec2[] {
  const o = project(origin);
  if (!o) return [];
  const PROBE = 100;
  const pxPerMm = Math.max(0, ...(['x', 'y'] as const).map((a) => {
    const d = GIZMO_AXES[a].dir, t = project({ x: origin.x + d.x * PROBE, y: origin.y + d.y * PROBE, z: origin.z });
    return t ? Math.hypot(t.x - o.x, t.y - o.y) / PROBE : 0;
  }));
  if (!(pxPerMm > 0)) return [];
  const r = RING_PX / pxPerMm, out: Vec2[] = [];
  for (let i = 0; i < RING_SEGMENTS; i++) {
    const a = (2 * Math.PI * i) / RING_SEGMENTS;
    const p = project({ x: origin.x + r * Math.cos(a), y: origin.y + r * Math.sin(a), z: origin.z });
    if (!p) return [];
    out.push(p);
  }
  return out;
}

/** Whether `p` is on the ring, within a press's reach. */
export function onRing(ring: readonly Vec2[], p: Vec2): boolean {
  return ring.some((a, i) => distToSegment(p, a, ring[(i + 1) % ring.length]) <= GIZMO_HIT_PX);
}

/** A turn's angle snapped: Alt to the whole degree, else to `TURN_STEP`. */
export const snapTurn = (deg: number, free: boolean): number => (free ? Math.round(deg) : Math.round(deg / TURN_STEP) * TURN_STEP);

/** The angle (degrees, −180 to 180) from `a` to `b`, both seen from `hub`. */
export function turnBetween(hub: Vec2, a: Vec2, b: Vec2): number {
  const d = ((Math.atan2(b.y - hub.y, b.x - hub.x) - Math.atan2(a.y - hub.y, a.x - hub.x)) * 180) / Math.PI;
  return d > 180 ? d - 360 : d <= -180 ? d + 360 : d;
}

/** What a turn reads: its angle, signed (`+45°`). */
export function turnReadout(deg: number): string {
  const r = Math.round(deg);
  return `${r > 0 ? '+' : r < 0 ? '-' : ''}${Math.abs(r)}°`;
}

// ---- Snapping, as a drag on the plan does ----------------------------------------

/** A delta a move snaps to (mm from the press, along its axis): what it lands
 *  on — named in the readout — and where (drawing coordinates), for the marker. */
export interface GizmoCatch { d: number; name?: string; at: Pt3 }

/** Where a move along the plan direction `axis` (unit) lands one of the
 *  selection's points `moving` on one of `targets` — the plan's vertex snap, held
 *  to the axis: only a target within `perpTol` mm of a point's line counts, at
 *  the delta that brings the point beside it. A target with a height (3D snap
 *  points stand at their own objects' heights, one per height) is marked at the
 *  one of its heights nearest `z`, the selection's base; one without, at `z`. */
export function vertexCatches(moving: readonly Vec2[], targets: ReadonlyArray<Vec2 & { z?: number }>, axis: Vec2, perpTol: number, z: number, name: string): GizmoCatch[] {
  const out: GizmoCatch[] = [];
  const seen = new Map<string, GizmoCatch>();
  for (const v of moving) {
    for (const t of targets) {
      const rx = t.x - v.x, ry = t.y - v.y;
      if (Math.abs(axis.x * ry - axis.y * rx) > perpTol) continue;
      const d = rx * axis.x + ry * axis.y;
      const k = `${Math.round(d * 10)}`;
      const tz = t.z ?? z;
      const was = seen.get(k);
      if (was) {
        if (Math.abs(tz - z) < Math.abs(was.at.z - z)) was.at = { x: t.x, y: t.y, z: tz };
        continue;
      }
      const c = { d, name, at: { x: t.x, y: t.y, z: tz } };
      seen.set(k, c);
      out.push(c);
    }
  }
  return out;
}

/** Where a move along the plan direction `axis` (unit) brings one of the
 *  selection's outline corners onto a face it moves toward from its free side
 *  (`n` points into the free space: a wall's, a partition's, a bench's) — flush,
 *  the plan's room snap held to the axis. Drawn at height `z`. */
export function faceCatches(corners: readonly Vec2[], faces: ReadonlyArray<{ a: Vec2; b: Vec2; n: Vec2 }>, axis: Vec2, z: number, name: string): GizmoCatch[] {
  const out: GizmoCatch[] = [];
  for (const f of faces) {
    const ex = f.b.x - f.a.x, ey = f.b.y - f.a.y;
    const den = axis.x * ey - axis.y * ex;
    if (Math.abs(den) < 1e-9) continue;
    for (const c of corners) {
      const wx = f.a.x - c.x, wy = f.a.y - c.y;
      // In front of the face (behind it: through the wall already) — so the
      // corner can only reach it moving into it.
      if (-(wx * f.n.x + wy * f.n.y) < -0.5) continue;
      const d = (wx * ey - wy * ex) / den;
      const s = (wx * axis.y - wy * axis.x) / den;
      if (s < -1e-6 || s > 1 + 1e-6) continue; // its line misses the face
      out.push({ d, name, at: { x: c.x + axis.x * d, y: c.y + axis.y * d, z } });
    }
  }
  return out;
}

/** Where a move up brings the selection's base or top (`span`, mm above the
 *  ground) onto one of `heights` — each named as it is. Drawn over `at`. */
export function heightCatches(span: readonly [number, number], heights: ReadonlyArray<{ z: number; name?: string }>, at: Vec2): GizmoCatch[] {
  const out: GizmoCatch[] = [];
  for (const h of heights) {
    for (const e of span) out.push({ d: h.z - e, name: h.name, at: { x: at.x, y: at.y, z: h.z } });
  }
  return out;
}

/** A move's delta snapped: free (Alt) to the whole mm; else to the nearest catch
 *  within `tol` mm; else so the selection's coordinate `ref` on the axis (its
 *  corner on the plan, its base in height) lands on the zoom's round `step`, as
 *  the plan's grid snap lands a dragged selection's corner. */
export function snapGizmoDelta(raw: number, catches: readonly GizmoCatch[], tol: number, step: number, ref: number, free: boolean): { d: number; caught: GizmoCatch | null } {
  if (free) return { d: Math.round(raw), caught: null };
  let best: GizmoCatch | null = null;
  for (const c of catches) if (Math.abs(c.d - raw) <= tol && (!best || Math.abs(c.d - raw) < Math.abs(best.d - raw))) best = c;
  if (best) return { d: best.d, caught: best };
  const st = step > 0 ? step : 1;
  return { d: Math.round((ref + raw) / st) * st - ref, caught: null };
}

/** Paint the gizmo: the rotate ring (violet; lit in the selection yellow), each
 *  arrow in its colour, the one worked or under the pointer (`lit`) in the
 *  selection yellow; while one moves, a dashed guide from where its hub stood
 *  (`from`) to where it is, and a diamond on what it caught. */
export function drawGizmo(ctx: CanvasRenderingContext2D, arrows: readonly GizmoArrow[], lit: GizmoAxis | null, from: Vec2 | null, caught: Vec2 | null = null, ring: { pts: readonly Vec2[]; lit: boolean } | null = null): void {
  ctx.save();
  ctx.lineCap = 'round';
  if (ring && ring.pts.length > 2) {
    for (const [stroke, width] of [['rgba(18,20,24,0.6)', 4.5], [ring.lit ? '#ffd166' : '#a78bfa', 2]] as const) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = width;
      ctx.beginPath();
      ring.pts.forEach((q, i) => (i ? ctx.lineTo(q.x, q.y) : ctx.moveTo(q.x, q.y)));
      ctx.closePath();
      ctx.stroke();
    }
  }
  if (from && arrows.length) {
    const o = arrows[0].from;
    ctx.setLineDash([5, 4]);
    ctx.strokeStyle = 'rgba(18,20,24,0.7)';
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(from.x, from.y); ctx.lineTo(o.x, o.y); ctx.stroke();
    ctx.strokeStyle = 'rgba(255, 209, 102, 0.95)';
    ctx.lineWidth = 1.25;
    ctx.stroke();
    ctx.setLineDash([]);
  }
  for (const g of arrows) {
    const dx = g.to.x - g.from.x, dy = g.to.y - g.from.y, l = Math.hypot(dx, dy) || 1;
    const ux = dx / l, uy = dy / l, head = Math.min(14, l * 0.35);
    const base = { x: g.to.x - ux * head, y: g.to.y - uy * head };
    const color = g.axis === lit ? '#ffd166' : GIZMO_AXES[g.axis].color;
    // A dark rim first, so the arrow reads over any colour of the model.
    for (const [stroke, width] of [['rgba(18,20,24,0.75)', 5], [color, 2.5]] as const) {
      ctx.strokeStyle = stroke;
      ctx.lineWidth = width;
      ctx.beginPath(); ctx.moveTo(g.from.x, g.from.y); ctx.lineTo(base.x, base.y); ctx.stroke();
    }
    ctx.beginPath();
    ctx.moveTo(g.to.x, g.to.y);
    ctx.lineTo(base.x - uy * head * 0.45, base.y + ux * head * 0.45);
    ctx.lineTo(base.x + uy * head * 0.45, base.y - ux * head * 0.45);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.strokeStyle = 'rgba(18,20,24,0.75)';
    ctx.lineWidth = 1;
    ctx.fill();
    ctx.stroke();
  }
  if (caught) {
    const r = 6;
    ctx.beginPath();
    ctx.moveTo(caught.x, caught.y - r); ctx.lineTo(caught.x + r, caught.y); ctx.lineTo(caught.x, caught.y + r); ctx.lineTo(caught.x - r, caught.y);
    ctx.closePath();
    ctx.fillStyle = '#aaff00';
    ctx.strokeStyle = 'rgba(18,20,24,0.85)';
    ctx.lineWidth = 1.5;
    ctx.fill();
    ctx.stroke();
  }
  if (arrows.length) {
    const o = arrows[0].from;
    ctx.beginPath();
    ctx.arc(o.x, o.y, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#e6eefc';
    ctx.strokeStyle = 'rgba(18,20,24,0.75)';
    ctx.lineWidth = 1.5;
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}
